import { useCallback, useEffect, useRef, useState } from "react";
import { ErrorState, LoadingState } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useMintThemePreviewToken } from "../services/useMintThemePreviewToken";

/**
 * Live storefront preview of a CATALOG theme version.
 *
 * The seller editor's mechanism does not transfer: its preview token is scoped
 * to a store and a catalog theme has no store. The full reasoning — and why the
 * other three options were rejected — is in
 * `docs/plans/2026-07-20-admin-theme-studio.md` §1. In short:
 *
 *   1. The buyer can only render against a real store (a theme's page is mostly
 *      product surface, and an unresolvable `$category:N` slot DROPS its whole
 *      section), and `apps/buyer` cannot be changed.
 *   2. So the admin picks a RENDER TARGET store, which contributes only its
 *      catalogue.
 *   3. The token names the catalog VERSION plus that store. The backend serves
 *      the catalog json in the `draft` slot of the endpoint the buyer already
 *      calls, so the buyer is byte-for-byte unchanged and NOTHING is written to
 *      the store.
 *
 * **The preview shows the last SAVED version, not the live editor state**, and
 * that is a property of the design rather than an omission: the preview reads
 * the persisted `theme_versions` row. The studio shows an explicit "unsaved
 * changes" affordance instead of silently rendering something stale.
 */

const DEVICES = {
  mobile: { w: 390, h: 844 },
  tablet: { w: 768, h: 1024 },
  desktop: { w: 1280, h: 900 },
} as const;

export type PreviewDevice = keyof typeof DEVICES;

/**
 * The buyer origin for a store slug. Admin is served from `admin.<tail>`; the
 * storefront lives at `<slug>.<tail>` behind the same wildcard cert.
 */
function resolveBuyerOrigin(storeSlug: string): string {
  if (typeof window === "undefined") return "";
  const { protocol, host } = window.location;
  if (host.startsWith("admin.")) {
    return `${protocol}//${storeSlug}.${host.slice("admin.".length)}`;
  }
  return window.location.origin;
}

interface StudioPreviewProps {
  themeId: string;
  versionId: string;
  storeSlug: string | null;
  device: PreviewDevice;
  locale: "ar" | "en";
  /** Bumped by the studio after a successful save to force a reload. */
  reloadKey: number;
}

export function StudioPreview({
  themeId,
  versionId,
  storeSlug,
  device,
  locale,
  reloadKey,
}: StudioPreviewProps) {
  const t = useTranslations();
  const { mutateAsync: mint } = useMintThemePreviewToken();
  const [token, setToken] = useState<string | null>(null);
  const [previewRetry, setPreviewRetry] = useState(0);
  const [failed, setFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [zoom, setZoom] = useState(1);

  const { w, h } = DEVICES[device];

  // Mint for this target, then renew shortly before the returned expiry.
  useEffect(() => {
    if (!storeSlug) return;
    let cancelled = false;
    let failed = false;
    let inFlight = false;
    let renewal: ReturnType<typeof setTimeout> | undefined;
    const refresh = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      clearTimeout(renewal);
      try {
        const result = await mint({ themeId, versionId, storeSlug });
        if (!cancelled) {
          setToken(result.token);
          failed = false;
          setFailed(false);
          // Server-relative lifetime survives an incorrect device clock.
          const remaining = Date.parse(result.expiresAt) -
            (result.serverTime ? Date.parse(result.serverTime) : Date.now());
          if (remaining > 0) renewal = setTimeout(() => void refresh(), Math.max(1000, remaining - 30_000));
        }
      } catch {
        // Do not fall back to the tokenless storefront URL: that would
        // render the STORE's own live theme, which
        // looks like a working preview of the wrong thing. Fail visibly.
        failed = true;
        if (!cancelled) {
          setToken(null);
          setFailed(true);
        }
      } finally { inFlight = false; }
    };
    const recover = () => { if (failed) void refresh(); };
    window.addEventListener("focus", recover);
    window.addEventListener("online", recover);
    void refresh();
    return () => {
      cancelled = true;
      clearTimeout(renewal);
      window.removeEventListener("focus", recover);
      window.removeEventListener("online", recover);
    };
    // `mint` is a fresh closure each render; including it would re-mint in a
    // loop. The identifying inputs are the three below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeId, versionId, storeSlug, reloadKey, previewRetry]);

  const [availableH, setAvailableH] = useState(0);

  // Fit the device frame to the available WIDTH, never upscaling past 1:1 —
  // the standard Shopify/Framer preview behaviour, and it means there is never
  // horizontal overflow to pan through.
  const recompute = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const available = rect.width - 24;
    if (available <= 0) return;
    setZoom(Math.max(0.15, Math.min(1, Number((available / w).toFixed(3)))));
    setAvailableH(Math.max(0, rect.height - 24));
  }, [w]);

  useEffect(() => {
    recompute();
    if (typeof ResizeObserver === "undefined") return;
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [recompute]);

  // The frame's LAYOUT height is derived from the pane so the scaled frame
  // exactly fills it (`frameH * zoom === availableH`) instead of a short
  // fixed-aspect frame stranded above dead space. The trade — the same one the
  // seller canvas makes — is that the simulated viewport gets tall when the
  // pane is tall; nothing is cropped, only viewport-relative CSS resolves
  // against a viewport no visitor has. Falls back to the device's natural
  // height until the pane has been measured once, so there is no flash of a
  // zero-height frame.
  const frameH =
    availableH > 0 && zoom > 0 ? Math.max(320, availableH / zoom) : h;

  const src =
    storeSlug && token
      ? `${resolveBuyerOrigin(storeSlug)}/${locale}?preview=${encodeURIComponent(token)}`
      : null;

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full items-start justify-center overflow-auto bg-field p-3"
    >
      {!storeSlug ? (
        <p className="mt-10 max-w-xs text-center text-secondary text-muted">
          {t(keys.themes.studioPreviewPickStore)}
        </p>
      ) : failed ? (
        <ErrorState
          title={t(keys.themes.studioPreviewFailed)}
          retryLabel={t(keys.shell.retry)}
          onRetry={() => setPreviewRetry(value => value + 1)}
        />
      ) : !src ? (
        <div style={{ width: "100%", maxWidth: 520, marginTop: 40 }}>
          <LoadingState
            title={t(keys.shell.loadingTitle, { page: t("themes.previewPageName") })}
            sub={t(keys.shell.loadingSub)}
            rows={0}
          />
        </div>
      ) : (
        <div
          style={{
            width: w * zoom,
            height: frameH * zoom,
          }}
        >
          <iframe
            // `reloadKey` in the key forces a genuine re-mount after a save.
            // A `src` reassignment alone would not refetch, because the URL is
            // unchanged while the token is still valid.
            key={`${device}-${storeSlug}-${reloadKey}-${token}`}
            src={src}
            title={t(keys.themes.studioPreviewTitle)}
            className="origin-top-left rounded-card border border-border bg-white shadow-sm"
            style={{
              width: w,
              height: frameH,
              transform: `scale(${zoom})`,
            }}
          />
        </div>
      )}
    </div>
  );
}
